import { PropsWithChildren } from 'react';

import { MetricSkeleton } from '@components/patterns/Metric/Skeleton';
import { cn } from '@components/primitives/utilities';

import { Change } from './Change';

import css from './styles.module.css';

// Sorted from largest to smallest
const SUFFIXES = [
  { min: 1_000_000_000_000, key: 'T' },
  { min: 1_000_000_000, key: 'B' },
  { min: 1_000_000, key: 'M' },
  { min: 1_000, key: 'K' },
];

export function formatValue(value: number | string) {
  if (typeof value === 'string') {
    return value;
  }

  const suffix = SUFFIXES.find((s) => value >= s.min);
  if (suffix) {
    const count = (value / suffix.min).toFixed(1).replace('.0', '');
    return `${count}${suffix.key}`;
  }

  return value
    .toLocaleString('en-US', { maximumFractionDigits: 1 })
    .replace('.0', '');
}

export interface MetricProps extends PropsWithChildren {
  className?: string;
  value: number | string;
  variant?: 'primary' | 'danger';
}

/** Children render beside the value — typically a `Metric.Change`. */
function Metric({ value, children, className, variant }: MetricProps) {
  return (
    <div className={cn(css.row, className)}>
      <p className={css.metric}>
        <span className={css.value} data-variant={variant}>
          {formatValue(value)}
        </span>
      </p>

      {children}
    </div>
  );
}

Metric.Change = Change;
Metric.Skeleton = MetricSkeleton;

export { Metric };
